import React, { useState, useEffect } from "react";
import { Modal, Tag, Button } from "antd";
import { ClockCircleOutlined, CarOutlined, ShoppingOutlined } from "@ant-design/icons";
import NumberFormat from "react-number-format";

const DriverPendingModal = ({ visible, onClose, activeDeliveries = [], onViewOrder, onRefresh }) => {
  const handleItemViewOrder = (item) => {
    if (typeof onRefresh === "function") {
      onRefresh();
    }
    if (onClose) onClose();
    if (onViewOrder) onViewOrder(item);
  };

  useEffect(() => {
    if (!visible) return;
    const interval = setInterval(() => {
      if (typeof onRefresh === "function") {
        onRefresh();
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [visible, onRefresh]);

  const safeDeliveries = Array.isArray(activeDeliveries)
    ? activeDeliveries
    : Array.isArray(activeDeliveries?.deliveries)
      ? activeDeliveries.deliveries
      : Array.isArray(activeDeliveries?.orders)
        ? activeDeliveries.orders
        : [];

  const deliveriesToAccept = safeDeliveries.filter(
    (d) => d.deliveryStatus === "WAITING" || d.status === "WAITING" || d.delivery_status === "pending"
  );
  const deliveriesForPickup = safeDeliveries.filter(
    (d) => d.deliveryStatus === "ACCEPTED" || d.status === "READY" || d.delivery_status === "ready"
  );
  const deliveriesInTransit = safeDeliveries.filter(
    (d) => d.deliveryStatus === "IN_TRANSIT" || d.delivery_status === "in_transit" || d.status === "IN_TRANSIT"
  );

  const availableTabs = [
    { key: "accept", label: "Order to Accept", count: deliveriesToAccept.length, icon: <ClockCircleOutlined /> },
    { key: "pickup", label: "For Pickup", count: deliveriesForPickup.length, icon: <ShoppingOutlined /> },
    { key: "in-transit", label: "In Transits", count: deliveriesInTransit.length, icon: <CarOutlined /> },
  ].filter((t) => t.count > 0);

  const [activeTab, setActiveTab] = useState(() => availableTabs[0]?.key || "accept");

  useEffect(() => {
    if (availableTabs.length > 0) {
      if (!availableTabs.some((t) => t.key === activeTab)) {
        setActiveTab(availableTabs[0].key);
      }
    }
  }, [availableTabs, activeTab]);

  useEffect(() => {
    if (visible && availableTabs.length === 0) {
      if (onClose) onClose();
    }
  }, [visible, availableTabs.length]);

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <ClockCircleOutlined className="text-gray-800 text-xl" />
          <span className="font-gilroyBold text-lg text-gray-900">Driver Pending Activities</span>
        </div>
      }
      open={visible && availableTabs.length > 0}
      onCancel={onClose}
      centered
      footer={[
        <Button key="close" type="primary" onClick={onClose} className="bg-black hover:bg-gray-800 text-white border-none px-5 rounded-xl h-10 text-[14px] font-gilroyBold">
          Close
        </Button>,
      ]}
      width="92%"
      style={{ maxWidth: "520px" }}
      bodyStyle={{ maxHeight: "75vh", overflowY: "auto", padding: "14px" }}
    >
      <div className="pt-1 space-y-4">
        {availableTabs.length > 0 && (
          <div
            className="flex gap-2 border-b border-gray-200 pb-3 overflow-x-auto scroll-smooth [&::-webkit-scrollbar]:hidden"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {availableTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-3.5 py-2 rounded-xl text-[14px] font-gilroyBold whitespace-nowrap transition-all flex items-center gap-1.5 shrink-0 ${
                  activeTab === tab.key
                    ? "bg-black text-white border border-black"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {tab.icon}
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
        )}

        {activeTab === "accept" && (
          <div className="space-y-4">
            {deliveriesToAccept.length > 0 ? (
              deliveriesToAccept.map((item, idx) => (
                <div key={idx} className="p-4 sm:p-5 border border-gray-200 rounded-2xl bg-gray-50/50 space-y-3">
                  <div className="flex items-center justify-between gap-2 border-b border-gray-200 pb-3">
                    <span className="text-[13px] text-gray-500 font-gilroyBold uppercase tracking-wider">
                      DELIVERY ASSIGNMENT
                    </span>
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => handleItemViewOrder(item)}
                      className="bg-blue-600 hover:bg-blue-700 text-white border-none rounded-xl px-4 py-1 text-[13px] font-gilroyBold shrink-0"
                    >
                      View
                    </Button>
                  </div>
                  <div className="font-gilroyBold text-4xl sm:text-5xl text-black font-black tracking-widest my-2 py-1 break-all">
                    {item.orderCode || item.orderRef || item.id || "N/A"}
                  </div>
                  <div className="text-[14px] text-gray-700 pt-1">
                    New delivery assignment waiting for driver acceptance.
                  </div>
                </div>
              ))
            ) : (
              <div className="py-8 text-center text-gray-500 text-[14px] font-gilroyBold">No delivery requests waiting for acceptance.</div>
            )}
          </div>
        )}

        {activeTab === "pickup" && (
          <div className="space-y-4">
            {deliveriesForPickup.length > 0 ? (
              deliveriesForPickup.map((item, idx) => (
                <div key={idx} className="p-4 sm:p-5 border border-gray-200 rounded-2xl bg-gray-50/50 space-y-3">
                  <div className="flex items-center justify-between gap-2 border-b border-gray-200 pb-3">
                    <span className="text-[13px] text-gray-500 font-gilroyBold uppercase tracking-wider">
                      ORDER CODE / PICKUP CODE
                    </span>
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => handleItemViewOrder(item)}
                      className="bg-blue-600 hover:bg-blue-700 text-white border-none rounded-xl px-4 py-1 text-[13px] font-gilroyBold shrink-0"
                    >
                      View
                    </Button>
                  </div>

                  <div className="font-gilroyBold text-4xl sm:text-5xl text-black font-black tracking-widest my-2 py-1 break-all">
                    {item.orderCode || item.orderRef || item.id || "N/A"}
                  </div>

                  <div className="text-[14px] text-gray-700 pt-1">
                    Present this pickup code at the vendor location to collect package.
                  </div>
                </div>
              ))
            ) : (
              <div className="py-8 text-center text-gray-500 text-[14px] font-gilroyBold">No orders waiting for rider pickup.</div>
            )}
          </div>
        )}

        {activeTab === "in-transit" && (
          <div className="space-y-4">
            {deliveriesInTransit.length > 0 ? (
              deliveriesInTransit.map((item, idx) => (
                <div key={idx} className="p-4 sm:p-5 border border-gray-200 rounded-2xl bg-gray-50/50 space-y-3">
                  <div className="flex items-center justify-between gap-2 border-b border-gray-200 pb-3">
                    <span className="text-[13px] text-gray-500 font-gilroyBold uppercase tracking-wider">
                      IN-TRANSIT DELIVERY
                    </span>
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => handleItemViewOrder(item)}
                      className="bg-blue-600 hover:bg-blue-700 text-white border-none rounded-xl px-4 py-1 text-[13px] font-gilroyBold shrink-0"
                    >
                      View
                    </Button>
                  </div>
                  <div className="font-gilroyBold text-4xl sm:text-5xl text-black font-black tracking-widest my-2 py-1 break-all">
                    {item.orderCode || item.orderRef || item.id || "N/A"}
                  </div>
                  <div className="text-[14px] text-gray-700 pt-1">
                    Delivery rider currently en route to customer destination.
                  </div>
                </div>
              ))
            ) : (
              <div className="py-8 text-center text-gray-500 text-[14px] font-gilroyBold">No deliveries currently in-transit.</div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default DriverPendingModal;
